import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { InputField } from './InputField';
import { FormFooter } from './FormFooter';
import { UniqueFieldNotice } from './UniqueFieldNotice';

interface EditEmailScreenProps {
  onBack: () => void;
  onSave: (email: string) => void;
  currentEmail: string;
}

export const EditEmailScreen = ({ onBack, onSave, currentEmail }: EditEmailScreenProps) => {
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ email: '', confirmEmail: '' });

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSave = () => {
    const newErrors = { email: '', confirmEmail: '' };

    // Validaciones
    if (!email.trim()) {
      newErrors.email = 'Ingresa tu nuevo correo';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Ingresa un correo válido';
    }

    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = 'Confirma tu nuevo correo';
    } else if (email !== confirmEmail) {
      newErrors.confirmEmail = 'Los correos no coinciden';
    }

    setErrors(newErrors);

    if (newErrors.email || newErrors.confirmEmail) {
      return;
    }

    setIsLoading(true);

    // Simular guardado
    setTimeout(() => {
      onSave(email);
      setIsLoading(false);
    }, 1000);
  };

  const isFormValid = email && confirmEmail && email === confirmEmail && validateEmail(email);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Mobile App Bar */}
      <div className="lg:hidden bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-[#111827]" />
          </button>
          <h1 className="text-lg font-bold text-[#111827]">Editar correo</h1>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#6B7280] hover:text-[#111827] transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver al perfil</span>
          </button>
          <h1 className="text-2xl font-bold text-[#111827]">Editar correo electrónico</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto p-4 lg:p-6 space-y-6">
        <UniqueFieldNotice message="Este correo se usa para enviarte tus boletos y notificaciones importantes. Podemos solicitar verificación por correo." />

        <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 space-y-6">
          {/* Current Email */}
          <div className="pb-4 border-b border-[#E5E7EB]">
            <label className="block text-sm font-semibold text-[#6B7280] mb-2">
              Correo actual
            </label>
            <p className="font-medium text-[#111827]">{currentEmail}</p>
          </div>

          <InputField
            label="Nuevo correo electrónico"
            value={email}
            onChange={(val) => {
              setEmail(val);
              setErrors({ ...errors, email: '' });
            }}
            type="email"
            placeholder="ejemplo@correo.com"
            error={errors.email}
            disabled={isLoading}
          />

          <InputField
            label="Confirmar nuevo correo"
            value={confirmEmail}
            onChange={(val) => {
              setConfirmEmail(val);
              setErrors({ ...errors, confirmEmail: '' });
            }}
            type="email"
            placeholder="ejemplo@correo.com"
            error={errors.confirmEmail}
            disabled={isLoading}
          />

          <FormFooter
            onCancel={onBack}
            onSave={handleSave}
            saveLabel="Guardar cambios"
            isLoading={isLoading}
            disabled={!isFormValid}
          />
        </div>
      </div>
    </div>
  );
};
